/**
 * Data Encryption Module
 *
 * This module handles encryption and decryption of data using AES-256-GCM.
 * All data is encrypted client-side before being sent to the server.
 */

import { arrayBufferToBase64, base64ToArrayBuffer } from './keys';

// Encryption format: base64(iv) + ':' + base64(encryptedData + authTag)

/**
 * Encrypt string data with AES-256-GCM
 */
export async function encryptData(
  data: string,
  aesKey: CryptoKey
): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);

  // Generate random IV (12 bytes for GCM)
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt the data
  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    aesKey,
    dataBuffer
  );

  // Format: iv:encryptedData (both base64)
  const ivBase64 = arrayBufferToBase64(iv.buffer);
  const dataBase64 = arrayBufferToBase64(encryptedBuffer);

  return `${ivBase64}:${dataBase64}`;
}

/**
 * Decrypt string data with AES-256-GCM
 */
export async function decryptData(
  encryptedString: string,
  aesKey: CryptoKey
): Promise<string> {
  const [ivBase64, dataBase64] = encryptedString.split(':');

  if (!ivBase64 || !dataBase64) {
    throw new Error('Invalid encrypted data format');
  }

  const iv = new Uint8Array(base64ToArrayBuffer(ivBase64));
  const encryptedData = base64ToArrayBuffer(dataBase64);

  // Decrypt the data
  const decryptedBuffer = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    aesKey,
    encryptedData
  );

  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}

/**
 * Encrypt JSON object
 */
export async function encryptJSON<T>(
  data: T,
  aesKey: CryptoKey
): Promise<string> {
  const jsonString = JSON.stringify(data);
  return encryptData(jsonString, aesKey);
}

/**
 * Decrypt JSON object
 */
export async function decryptJSON<T>(
  encryptedString: string,
  aesKey: CryptoKey
): Promise<T> {
  const jsonString = await decryptData(encryptedString, aesKey);
  return JSON.parse(jsonString) as T;
}

/**
 * Encrypt binary data (for photos)
 */
export async function encryptBinary(
  data: ArrayBuffer,
  aesKey: CryptoKey
): Promise<ArrayBuffer> {
  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt the data
  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    aesKey,
    data
  );

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encryptedBuffer), iv.length);

  return combined.buffer;
}

/**
 * Decrypt binary data (for photos)
 */
export async function decryptBinary(
  encryptedData: ArrayBuffer,
  aesKey: CryptoKey
): Promise<ArrayBuffer> {
  const combined = new Uint8Array(encryptedData);

  // Extract IV and encrypted data
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);

  // Decrypt the data
  return crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    aesKey,
    data
  );
}

/**
 * Encrypt a file (for photo uploads)
 */
export async function encryptFile(
  file: File,
  aesKey: CryptoKey
): Promise<Blob> {
  const fileBuffer = await file.arrayBuffer();
  const encryptedBuffer = await encryptBinary(fileBuffer, aesKey);
  return new Blob([encryptedBuffer], { type: 'application/octet-stream' });
}

/**
 * Decrypt a file (for photo downloads)
 */
export async function decryptFile(
  encryptedBlob: Blob,
  aesKey: CryptoKey,
  mimeType: string
): Promise<Blob> {
  const encryptedBuffer = await encryptedBlob.arrayBuffer();
  const decryptedBuffer = await decryptBinary(encryptedBuffer, aesKey);
  return new Blob([decryptedBuffer], { type: mimeType });
}

/**
 * Create object URL for decrypted image
 */
export async function createDecryptedImageUrl(
  encryptedBlob: Blob,
  aesKey: CryptoKey,
  mimeType: string
): Promise<string> {
  const decryptedBlob = await decryptFile(encryptedBlob, aesKey, mimeType);
  return URL.createObjectURL(decryptedBlob);
}

/**
 * Helper to encrypt baby profile data
 */
export interface BabyProfileData {
  name: string;
  profileType?: 'baby' | 'pregnancy';
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other';
  photoUrl?: string;
  pediatrician?: {
    name: string;
    phone?: string;
    address?: string;
  };
  // Pregnancy-specific
  dueDate?: string;
  prePregnancyWeight?: number;
  prePregnancyWeightUnit?: 'kg' | 'lbs';
  obGyn?: {
    name: string;
    phone?: string;
    address?: string;
  };
  allergies?: string[];
  medications?: string[];
  bloodType?: string;
  notes?: string;
  dailyFeedTargetMl?: number;
  dailyFeedTargetMinMl?: number;
  dailyFeedTargetMaxMl?: number;
  postFeedUprightMinutes?: number;
}

export async function encryptBabyProfile(
  data: BabyProfileData,
  aesKey: CryptoKey
): Promise<string> {
  return encryptJSON(data, aesKey);
}

export async function decryptBabyProfile(
  encryptedData: string,
  aesKey: CryptoKey
): Promise<BabyProfileData> {
  return decryptJSON<BabyProfileData>(encryptedData, aesKey);
}

/**
 * Helper to encrypt event data
 */
export interface EventData {
  // Feed event
  feedType?: 'breast' | 'formula' | 'both' | 'bottle'; // bottle for backwards compatibility
  side?: 'left' | 'right' | 'both';
  duration?: number; // minutes
  amount?: number; // ml or oz
  startTime?: string;
  endTime?: string;

  // Nappy event
  wet?: boolean;
  dirty?: boolean;
  rash?: boolean;
  rashSeverity?: number;
  blowout?: boolean;

  // Spit up event
  severity?: 'little' | 'some' | 'lots';

  // Medication event
  medicationName?: string;
  dose?: string;
  nextDue?: string;

  // Sleep event
  sleepType?: 'night' | 'nap';
  location?: string;

  // Weight event
  weight?: number;
  unit?: 'kg' | 'lbs';
  height?: number;
  heightUnit?: 'cm' | 'inches';
  headCircumference?: number;

  // Temperature event
  temperature?: number;
  tempUnit?: 'C' | 'F';
  method?: 'oral' | 'rectal' | 'forehead' | 'armpit' | 'ear';

  // Tummy time event
  mood?: 'happy' | 'neutral' | 'fussy';
  milestones?: string[];
  headUpDuration?: number; // seconds

  // EC (Elimination Communication)
  ecPee?: 'none' | 'little' | 'loads';
  ecPoop?: 'none' | 'little' | 'loads';

  // Pump event
  leftAmount?: number;
  rightAmount?: number;
  storageLocation?: string;

  // Bottle event
  bottleType?: 'formula' | 'expressed';
  amountOffered?: number;
  amountConsumed?: number;
  formulaBrand?: string;

  // Journal event
  title?: string;
  tags?: string[];
  isMilestone?: boolean;

  // Symptom (Pregnancy)
  symptomType?: string;
  symptomSeverity?: 'mild' | 'moderate' | 'severe';

  // Appointment (Pregnancy)
  appointmentType?: string;
  providerName?: string;
  appointmentLocation?: string;
  nextAppointmentDate?: string;

  // Blood Pressure (Pregnancy)
  systolic?: number;
  diastolic?: number;
  heartRate?: number;

  // Ultrasound (Pregnancy)
  ultrasoundType?: string;
  gestationalAgeWeeks?: number;
  fetalHeartRate?: number;
  ultrasoundFindings?: string;

  // Kick Count (Pregnancy)
  kickCount?: number;

  // Contraction (Pregnancy)
  contractionDuration?: number;
  contractionIntervalMinutes?: number;
  contractionIntensity?: 'mild' | 'moderate' | 'strong';

  // Common
  notes?: string;
}

export async function encryptEventData(
  data: EventData,
  aesKey: CryptoKey
): Promise<string> {
  return encryptJSON(data, aesKey);
}

export async function decryptEventData(
  encryptedData: string,
  aesKey: CryptoKey
): Promise<EventData> {
  return decryptJSON<EventData>(encryptedData, aesKey);
}

/**
 * Helper to encrypt emergency access packet
 */
export interface EmergencyPacket {
  babyName: string;
  dateOfBirth: string;
  bloodType?: string;
  allergies: string[];
  medications: string[];
  pediatrician?: {
    name: string;
    phone?: string;
    address?: string;
  };
  emergencyNotes?: string;
}

export async function encryptEmergencyPacket(
  data: EmergencyPacket,
  aesKey: CryptoKey
): Promise<string> {
  return encryptJSON(data, aesKey);
}

export async function decryptEmergencyPacket(
  encryptedData: string,
  aesKey: CryptoKey
): Promise<EmergencyPacket> {
  return decryptJSON<EmergencyPacket>(encryptedData, aesKey);
}
